import { ColumnsType } from "antd/es/table"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { SystemDescriptions } from "src/common/descriptions"
import { ServiceContract } from "src/common/models/contract"
import { ServiceContractDebitRecordsFilters, ServiceDebitContract, ServiceDebitStatus } from "src/common/models/serviceContractDebit"
import { SearchableTable } from "src/components/SearchableTable"
import { cleanCurrentContract, getContractDebitByIdApi, getServiceContractByIdApi, getServiceContractDebitsApi } from "src/features/ServiceContract/state/action"
import { GET_CONTRACT_DEBIT_BY_ID_API, GET_SERVICE_CONTRACT_DEBITS_API } from "src/features/ServiceContract/state/actionTypes"
import { RootState } from "src/state/reducer"
import { ColumnsKeys, summaryColumns, TABLE_COLUMNS } from "./config"
import { searchableTableUtils } from "src/components/SearchableTable/utils"
import { Button, Popconfirm, Space, Tooltip, notification } from "antd"
import { ProfileOutlined, CreditCardOutlined, SendOutlined } from '@ant-design/icons'
import { ContractConfirmation } from "src/features/ServiceContract/CreateServiceContract/ContractConfirmation"
import { CreatePayment } from "src/features/ServiceContract/CreateServiceContract/TicketSetction/CreatePayment"
import { Loader } from "src/components/Loader"
import { useParams } from "react-router"
import { sendAccountBalanceApi } from "src/features/Customer/state/action"

export type ReduxProps = {
    serviceContractDebits?: ServiceDebitContract[],
    isGettingContractDebits: boolean
    currentContract?: Partial<ServiceContract>
    isGettingContract: boolean
    isGettingContractDebit: boolean
    currentContractDebit?: ServiceDebitContract
}

interface Props {
    serviceContractId?: number
}

export const ServiceContractDebitsComponent = (props: Props) => {
    const dispatch = useDispatch()
    const { id: customerId } = useParams<{ id: string }>();

    const [selectedAction, setSelectedAction] = useState<string | undefined>()

    useEffect(() => {
        initData()
    }, [])

    const initData = () => {
        const payload: ServiceContractDebitRecordsFilters = {
            status: ServiceDebitStatus.PENDING_PAYMENT,
        }
        if (props.serviceContractId) {
            payload.serviceContractId = props.serviceContractId
        } else if (customerId) {
            payload.customerId = Number(customerId)
        }

        dispatch(getServiceContractDebitsApi(payload))
    }

    const descriptions = SystemDescriptions.PAGES.SERVICE_CONTRACT.SERVICE_CONTRACT_DEBITS
    const commonDescriptions = SystemDescriptions.PAGES.COMMON
    const createDescriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT

    const reduxProps: ReduxProps = useSelector((state: RootState) => {
        return ({
            serviceContractDebits: state.serviceContract.serviceContractDebits || [],
            isGettingContractDebits: serviceLoading(state, [GET_SERVICE_CONTRACT_DEBITS_API]),
            currentContract: state.serviceContract.currentContract,
            isGettingContract: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
            currentContractDebit: state.serviceContract.currentContractDebit,
            isGettingContractDebit: serviceLoading(state, [GET_CONTRACT_DEBIT_BY_ID_API]),
        })
    })

    const tblColumns: ColumnsType<ServiceContract> = [
        ...TABLE_COLUMNS,
        {
            title: "actions",
            dataIndex: ColumnsKeys.ACTIONS,
            key: ColumnsKeys.ACTIONS,
            align: searchableTableUtils.alignment.centerAlign,
            render: (_, record: ServiceDebitContract) => {
                const handleViewClick = (contractId: number) => {
                    setSelectedAction("VIEW")
                    dispatch(getServiceContractByIdApi(contractId))
                }

                const handleCreatePaymentClick = (contractId: number) => {
                    setSelectedAction("CREATE_PAYMENT")
                    dispatch(getContractDebitByIdApi(contractId))
                }

                const handleSendAccountBalanceClick = () => {
                    if (!customerId || !record.id) return

                    dispatch(sendAccountBalanceApi({
                        customerId: Number(customerId),
                        serviceContractDebitIds: [record.id],
                        isPartialAccountBalance: true,
                    }))
                }

                return <Space size="middle">
                    <Tooltip title={descriptions.VIEW_CONTRACT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<ProfileOutlined />}
                            onClick={() => handleViewClick(record.serviceContractId!)}
                        />
                    </Tooltip>
                    <Tooltip title={descriptions.CREATE_PAYMENT} >
                        <Button type="primary" ghost shape="circle" size="small"
                            icon={<CreditCardOutlined />}
                            onClick={() => handleCreatePaymentClick(record.id!)}
                        />
                    </Tooltip>
                    {
                        customerId &&
                        <Tooltip title={descriptions.SEND_ACCOUNT_BALANCE} >
                            <Popconfirm
                                placement="topLeft"
                                title={descriptions.SEND_ACCOUNT_BALANCE_CONFIRMATION}
                                description={""}
                                okText={commonDescriptions.YES}
                                cancelText={commonDescriptions.NO}
                                onConfirm={handleSendAccountBalanceClick}
                            >
                                <Button type="primary" ghost shape="circle" size="small"
                                    icon={<SendOutlined />}
                                />
                            </Popconfirm>
                        </Tooltip>
                    }
                </Space>
            },
        },
    ]

    const closeModal = () => {
        dispatch(cleanCurrentContract())
        setSelectedAction(undefined)
    }

    const openNotification = (message: string, description: string) => {
        notification.success({
            message,
            description,
        });
    };

    const renderLoader = () => (
        <Loader
            isVisible={reduxProps.isGettingContractDebit}
            title={descriptions.MAIN_ACTION_TITLE}
            description={descriptions.MAIN_ACTION_DESCRIPTION}
        />
    )

    return (
        <>
            <SearchableTable
                showSearch
                showLoader={reduxProps.isGettingContractDebits}
                items={reduxProps.serviceContractDebits || []}
                tableColumns={tblColumns as any}
                searchLabel={descriptions.TABLE.SEARCH_PLACEHOLDER}
                summaryColumns={summaryColumns}
            />
            {
                reduxProps.currentContract && selectedAction === "VIEW" &&
                <ContractConfirmation
                    open={!!reduxProps.currentContract}
                    onCancel={() => { closeModal() }}
                    onAccept={() => { closeModal() }}
                    okText={commonDescriptions.ACCEPT}
                    cancelText={commonDescriptions.CANCEL}
                    currentContract={reduxProps.currentContract}
                    type="SERVICE_CONTRACT"
                />
            }
            {
                reduxProps.currentContractDebit && selectedAction === "CREATE_PAYMENT" &&
                <CreatePayment
                    open={!!reduxProps.currentContractDebit}
                    onCancel={() => { closeModal() }}
                    onAccept={() => {
                        closeModal()
                        initData()
                        openNotification(
                            createDescriptions.SUCCESS_MODAL.TITLE,
                            createDescriptions.SUCCESS_MODAL.DESCRIPTION,
                        )
                    }}
                    contractDebit={reduxProps.currentContractDebit}
                />
            }
            {renderLoader()}
        </>
    )
}
